import React, { useState, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { Truck, X, Loader2, Search, ChevronRight } from 'lucide-react';
import api from '../../api/auth.api';

interface TruckTicket {
  ticket_id: number;
  ticket_number: string;
  created_at: string;
  material_type: string;
  weight: number;
  truck_company: string;
  truck_number: string;
  job_name: string;
  bradanna_job_number: string;
  ticket_photo_url?: string;
  truck_photo_url?: string;
  ocr_ticket_number?: string;
  ocr_weight?: string;
  ocr_vendor?: string;
  ocr_material?: string;
  ocr_truck_number?: string;
  ocr_stamp_detected?: boolean;
  ocr_stamp_color?: string;
  created_by_first_name: string;
  created_by_last_name: string;
  hauler_name: string;
}

// Get passed jobId from navigation
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};

export const TruckTicketViewer: React.FC = () => {
  const passedJobId = usePassedJobId();

  const [tickets, setTickets] = useState<TruckTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<TruckTicket | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [jobInfo, setJobInfo] = useState<{ job_name: string; bradanna_job_number: string } | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Format date/time
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Load tickets
  useEffect(() => {
    const fetchTickets = async () => {
      if (!passedJobId) {
        setError('No job selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/api/trucks?job_id=${passedJobId}`);
        if (response.data.success) {
          const ticketData = response.data.data;
          setTickets(ticketData);
          setTotalCount(ticketData.length);

          // Count today's tickets
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todaysTickets = ticketData.filter((t: TruckTicket) => new Date(t.created_at) >= today);
          setTodayCount(todaysTickets.length);

          if (ticketData.length > 0) {
            setJobInfo({
              job_name: ticketData[0].job_name,
              bradanna_job_number: ticketData[0].bradanna_job_number
            });
          }
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [passedJobId]);

  // Filter tickets by search
  const filteredTickets = tickets
    .filter(t => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return (
        t.ticket_number.toLowerCase().includes(search) ||
        (t.ocr_ticket_number && t.ocr_ticket_number.toLowerCase().includes(search)) ||
        (t.ocr_vendor && t.ocr_vendor.toLowerCase().includes(search)) ||
        t.truck_company.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Get stamp badge
  const getStampBadge = (ticket: TruckTicket) => {
    if (ticket.ocr_stamp_detected) {
      return (
        <span style={{
          padding: '2px 8px',
          backgroundColor: '#dcfce7',
          color: '#166534',
          fontSize: '11px',
          fontWeight: '600',
          borderRadius: '4px'
        }}>
          Stamped
        </span>
      );
    }
    return (
      <span style={{
        padding: '2px 8px',
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px'
      }}>
        No Stamp
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Loader2 size={40} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading tickets...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Truck Tickets
        </h1>
        {jobInfo && (
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            {jobInfo.job_name} • #{jobInfo.bradanna_job_number}
          </p>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        {/* KPI Stats - Today and Total */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            backgroundColor: '#3b82f6',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'white' }}>{todayCount}</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>Today</div>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b' }}>{totalCount}</div>
            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total</div>
          </div>
        </div>

        {/* Search */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Search by ticket # or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: '#1e293b',
              backgroundColor: 'transparent'
            }}
          />
          {searchTerm && (
            <X
              size={20}
              color="#94a3b8"
              onClick={() => setSearchTerm('')}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Ticket List */}
        {filteredTickets.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Truck size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
              {searchTerm ? 'No tickets found' : 'No tickets for this job'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredTickets.map(ticket => (
              <div
                key={ticket.ticket_id}
                onClick={() => { setSelectedTicket(ticket); setShowModal(true); }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    #{ticket.ocr_ticket_number || ticket.ticket_number}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                    {ticket.ocr_vendor || ticket.truck_company || 'Unknown Supplier'}
                  </div>
                </div>
                <ChevronRight size={20} color="#94a3b8" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {showModal && selectedTicket && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <div>
                <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                  #{selectedTicket.ocr_ticket_number || selectedTicket.ticket_number}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', margin: '4px 0 0' }}>
                  {formatDate(selectedTicket.created_at)} at {formatTime(selectedTicket.created_at)}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer'
                }}
              >
                <X size={24} color="white" />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '20px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
              {/* Details */}
              <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <DetailRow label="Supplier" value={selectedTicket.ocr_vendor || selectedTicket.truck_company || 'N/A'} />
                  <DetailRow label="Material" value={selectedTicket.ocr_material || selectedTicket.material_type || 'Unknown'} />
                  <DetailRow label="Weight" value={selectedTicket.ocr_weight || (selectedTicket.weight ? `${selectedTicket.weight} tons` : 'N/A')} />
                  <DetailRow label="Truck #" value={selectedTicket.ocr_truck_number || selectedTicket.truck_number || 'N/A'} />
                  <DetailRow label="Submitted By" value={`${selectedTicket.created_by_first_name} ${selectedTicket.created_by_last_name}`} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>Status</span>
                    {getStampBadge(selectedTicket)}
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedTicket.ticket_photo_url && (
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                      Ticket Photo
                    </h4>
                    <img
                      src={selectedTicket.ticket_photo_url}
                      alt="Ticket"
                      style={{ width: '100%', borderRadius: '12px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                )}
                {selectedTicket.truck_photo_url && (
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                      Truck Photo
                    </h4>
                    <img
                      src={selectedTicket.truck_photo_url}
                      alt="Truck"
                      style={{ width: '100%', borderRadius: '12px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Detail Row Component
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ color: '#64748b', fontSize: '14px' }}>{label}</span>
    <span style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500' }}>{value}</span>
  </div>
);
